'use client'

import type React from 'react'
import { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
export async function compressImage(file: File, maxSizeKB = 500): Promise<File> {
	return new Promise(resolve => {
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		if (!ctx) {
			// Fallback: return original file if canvas context is not available
			resolve(file)
			return
		}
		const img = new Image()

		img.onload = () => {
			// Calculate dimensions to keep aspect ratio but limit size
			const maxDimension = 1024 // Max width/height
			let { width, height } = img

			if (width > height) {
				if (width > maxDimension) {
					height = (height * maxDimension) / width
					width = maxDimension
				}
			} else {
				if (height > maxDimension) {
					width = (width * maxDimension) / height
					height = maxDimension
				}
			}

			canvas.width = width
			canvas.height = height

			ctx.drawImage(img, 0, 0, width, height)

			// Start with high quality and reduce if needed
			let quality = 0.9
			const tryCompress = () => {
				canvas.toBlob(
					blob => {
						if (blob) {
							const sizeKB = blob.size / 1024
							if (sizeKB > maxSizeKB && quality > 0.1) {
								quality -= 0.1
								tryCompress()
							} else {
								const compressedFile = new File([blob], file.name, {
									type: 'image/jpeg',
									lastModified: Date.now(),
								})
								resolve(compressedFile)
							}
						}
					},
					'image/jpeg',
					quality,
				)
			}
			tryCompress()
		}

		img.src = URL.createObjectURL(file)
	})
}

// Get human-readable file size
export function formatFileSize(bytes: number): string {
	if (bytes === 0) return '0 Bytes'
	const k = 1024
	const sizes = ['Bytes', 'KB', 'MB', 'GB']
	const i = Math.floor(Math.log(bytes) / Math.log(k))
	return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`
}

export // Helper function to create initial crop for profile photos (square aspect ratio)
function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
	return centerCrop(
		makeAspectCrop(
			{
				unit: 'px',
				width: Math.min(mediaWidth, mediaHeight),
				height: Math.min(mediaWidth, mediaHeight),
			},
			aspect,
			mediaWidth,
			mediaHeight,
		),
		mediaWidth,
		mediaHeight,
	)
}

export function getPixelCrop(imgRef: React.RefObject<HTMLImageElement | null>, crop: Crop) {
	// Get current image display and natural dimensions
	if (!imgRef.current) throw new Error('Image not loaded')
	const displayWidth = imgRef.current.width
	const displayHeight = imgRef.current.height
	const naturalWidth = imgRef.current.naturalWidth
	const naturalHeight = imgRef.current.naturalHeight

	let pixelCrop: {
		x: number
		y: number
		width: number
		height: number
		unit: 'px'
	}

	if (crop.unit === '%') {
		// Crop coordinates are in percentages (0-100%)
		// Convert directly to natural image coordinates
		pixelCrop = {
			x: Math.round((crop.x / 100) * naturalWidth),
			y: Math.round((crop.y / 100) * naturalHeight),
			width: Math.round((crop.width / 100) * naturalWidth),
			height: Math.round((crop.height / 100) * naturalHeight),
			unit: 'px' as const,
		}
	} else {
		// Crop coordinates are in pixels relative to display size
		// Scale them to natural image size
		const scaleX = naturalWidth / displayWidth
		const scaleY = naturalHeight / displayHeight

		pixelCrop = {
			x: Math.round(crop.x * scaleX),
			y: Math.round(crop.y * scaleY),
			width: Math.round(crop.width * scaleX),
			height: Math.round(crop.height * scaleY),
			unit: 'px' as const,
		}
	}

	if (pixelCrop.x + pixelCrop.width > naturalWidth || pixelCrop.y + pixelCrop.height > naturalHeight) {
		// Validate crop doesn't exceed bounds
		throw new Error('Crop exceeds image bounds!')
	}

	return pixelCrop
}

export const getCompressedImageToBase64 = (
	imgRef: React.RefObject<HTMLImageElement | null>,
	pixelCrop: { x: number; y: number; width: number; height: number },
) => {
	// Create cropped image and convert to base64
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')
	const fallback = ''
	if (ctx && imgRef.current) {
		try {
			// Set canvas size to crop dimensions
			canvas.width = pixelCrop.width
			canvas.height = pixelCrop.height

			// Draw the cropped portion of the image
			ctx.drawImage(imgRef.current, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height)

			// Convert canvas to base64
			const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1]
			return `data:image/jpeg;base64,${base64}`
		} catch (_error) {
			return `data:image/jpeg;base64,${fallback}`
		}
	} else {
		return `data:image/jpeg;base64,${fallback}`
	}
}

export const isStorageAvailable = () => {
	try {
		return window?.sessionStorage !== undefined
	} catch {
		return false
	}
}

export const manageImageCache = {
	save: ({ mode, base64 }: { mode: string; base64: string }) => {
		try {
			const key = `user-${mode}-image`
			sessionStorage.setItem(key, base64)
		} catch {
			return false
		}
	},

	load: ({ mode }: { mode: string }) => {
		try {
			const key = `user-${mode}-image`
			const image = sessionStorage.getItem(key)
			return image
		} catch (error) {
			console.error('Error loading image cache:', error)
			return null
		}
	},

	clear: ({ mode }: { mode: string }) => {
		try {
			const key = `user-${mode}-image`
			sessionStorage.removeItem(key)
		} catch {
			return false
		}
	},
}
